import React, {useEffect, useState} from 'react';
import Label from '@components/inputs/text/Label';
import { CurrentVehicleState } from './currentVehicleDetailsReducer';
import { NewVehicleState } from './newVehicleReducer';
import { ExchangeReasonState } from './exchangeReasonReducer';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import { Country } from '@utils/constants/localisation';
import { useNavigate, useParams } from 'react-router';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
    SignatureDocumentService,
    NewSignatureDocumentRequest,
    VehicleExchangeDocumentFields
} from '@services/signatureDocument/SignatureDocumentService';
import { SelectedSubscriptionState } from './selectedSubscriptionReducer';
import { HorizontalLineSep } from '@components/separators/horizontal_line';
import { AdditionalChargesState } from './additionalChargesReducer';
import { countrySpecificCurrency } from '@utils/misc/functions';
import Circular from '@components/spinners/Circular/Circular';
import Warning from '@components/modal/info/Warning';
import { logger as SentryLogger } from '@sentry/react';

interface Props {
    selectedSubscriptionState: SelectedSubscriptionState;
    currentVehicleState: CurrentVehicleState;
    newVehicleState: NewVehicleState;
    exchangeReasonState: ExchangeReasonState;
    additionalChargesState: AdditionalChargesState;
}

export default function ExchangeReview({
    selectedSubscriptionState,
    currentVehicleState,
    newVehicleState,
    exchangeReasonState,
    additionalChargesState
}: Props) {
    SentryLogger.info('ExchangeReview component rendered', {
        selectedSubscriptionState,
        currentVehicleState,
        newVehicleState,
        exchangeReasonState,
        additionalChargesState
    });

    const { custId, country } = useParams() as { custId: string, country: Country }
    const navigate = useNavigate();

     const [loading, setLoading] = useState<boolean>(false);
     const [errorVisible, setErrorVisible] = useState(false);
     const defaultErrorMessage = `We are sorry, an error occurred while processing your request. Click to return home.`;
     const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();


    type ChargeSectionProps = {
        label: string;
        value?: any;
    };

    const DisplayFieldValue: React.FC<ChargeSectionProps> = ({ label, value }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
                <Label text={label} styleCfg={{ bold: true }} />
            </div>
            <p style={{ margin: 0 }}>{value || ''}</p>
        </div>
    );

    const currentVehicle = currentVehicleState.vehicle;
    const newVehicle = newVehicleState.selectedVehicle;

    // Strict null/empty check for required fields
    if (!custId || !selectedSubscriptionState.selectedSubsId ||
        currentVehicleState.endingOdometer == null ||
        !currentVehicleState.fleetioStatusId || !newVehicleState.selectedVehicle?.id ||
        newVehicleState.startingOdometer == null || !exchangeReasonState.exchangeType ||
        !exchangeReasonState.exchangeReason) {
        // Error will be handled by parent checking that all this state is filled before rendering this component
        return null;
    }

    // Ensure current_vehicle_desired_status_id is an integer
    const currentVehicleDesiredStatusId = parseInt(currentVehicleState.fleetioStatusId, 10);
    if (isNaN(currentVehicleDesiredStatusId)) {
        // Error will be handled by parent checking that all this state is filled before rendering this component
        return null;
    }

    const document: VehicleExchangeDocumentFields = {
        document_type: 'vehicle_exchange',
        exchange_type: exchangeReasonState.exchangeType,
        exchange_reason: exchangeReasonState.exchangeReason,
        exchange_comments: exchangeReasonState.comments,
        subscription_id: selectedSubscriptionState.selectedSubsId,
        //Curr vehicle
        current_vehicle_id: currentVehicleState.vehicle.id,
        current_vehicle_ending_odometer: currentVehicleState.endingOdometer,
        current_vehicle_desired_status_id: currentVehicleDesiredStatusId,
        //New vehicle
        new_vehicle_id: newVehicleState.selectedVehicle.id,
        new_vehicle_starting_odometer: newVehicleState.startingOdometer,
        additional_charges: additionalChargesState.chargesList

    };

    // Get the logged in users details - these are used for the splend-representative signatory
    const userDetails = msalContext.instance.getActiveAccount()


    const payload: NewSignatureDocumentRequest = {
        customer_zoho_contact_id: custId,
        country: country,
        document: document,
        csr_name: userDetails?.name,
        csr_email: userDetails?.username
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);  // Intentionally don't set loading false after - only want them to click submit once.
        try {
            // 1. Create the signature document
            const createResp = await SignatureDocumentService.createSignatureDocument(
                payload,
                { msalContext, isAuthenticated }
            );
            const documentId = createResp.document_id;
            if (!documentId) throw new Error('No document_id returned');

            // 2. Get the document details (to get the sign_url)
            const detailsResp = await SignatureDocumentService.getSignatureDocumentDetails(
                documentId,
                country,
                { msalContext, isAuthenticated },
                "customer"
            );
            if (detailsResp && detailsResp.embed_sign_url) {
                // 3. Perform redirect to the embed sign URL
                window.location.replace(detailsResp.embed_sign_url);
            } else {
                throw new Error('No embed_sign_url returned');
            }
        } catch (err: any) {
            // Special case for axios 4xx errors - show user the error message
            if (err && err.response && err.response.status && err.response.status >= 400 && err.response.status < 500) {
                setErrorMessage(err.response.data?.error?.message
                    // If no error message from BE - cant show anything more useful than this
                    || `Unexpected API Error: (${err.response.status}. Cannot proceed.`);
            } else {
                setErrorMessage(defaultErrorMessage); //Default for a 400 if no message
            }
            setErrorVisible(true);
        }
    };

    return (
        <>
            {errorVisible && (
                <Warning message={errorMessage} additionalInfo="" onClickClose={() => { setErrorVisible(false); navigate('/'); }} />
            )}
            <NavBar pageTitle="Review" backAction={undefined} />
            <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={ handleSubmit}>
                <p>Please review ensuring all key details are correct ahead of processing cancellation</p>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <DisplayFieldValue label="Subscription Number" value={selectedSubscriptionState.selectedSubsId} />
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <HorizontalLineSep />
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <DisplayFieldValue label="Exchange Type" value={exchangeReasonState.exchangeType} />
                    <DisplayFieldValue label="Exchange Reason" value={exchangeReasonState.exchangeReason} />
                    <div>
                        <Label text="Additional Comments" styleCfg={{ bold: true }} />
                    </div>
                    <div style={{ overflowWrap: 'break-word' }}>
                        <p style={{ margin: 0 }}>{exchangeReasonState.comments}</p>
                    </div>
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <HorizontalLineSep />
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <DisplayFieldValue label="Current vehicle information:" value="" />

                    <DisplayFieldValue label="Make" value={currentVehicle.make} />

                    <DisplayFieldValue label="Model" value={currentVehicle.model} />

                    <DisplayFieldValue label="Year" value={currentVehicle.year} />

                    <DisplayFieldValue label="Color" value={currentVehicle.color} />

                    <DisplayFieldValue label="Registration plate" value={currentVehicle.license_plate} />

                    <DisplayFieldValue label="VIN" value={currentVehicle.vin} />

                    <DisplayFieldValue label="Body type" value={currentVehicle.body_type} />

                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <HorizontalLineSep />
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <DisplayFieldValue label="Charges:" value="" />
                    {additionalChargesState.chargesList.map((charge) => (
                        <div
                            key={charge.addonId}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <Label text={charge.addonLabel} />
                            <p style={{ margin: 0 }}>{countrySpecificCurrency(country)}{charge.addonTotalPrice}</p>
                        </div>
                    ))}
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <HorizontalLineSep />
                </div>

                <div style={{ margin: '2.5vh 0vw' }}>
                    <DisplayFieldValue label="New vehicle information:" value="" />

                    <DisplayFieldValue label="Make" value={newVehicle.make} />

                    <DisplayFieldValue label="Model" value={newVehicle.model} />

                    <DisplayFieldValue label="Year" value={newVehicle.year} />

                    <DisplayFieldValue label="Color" value={newVehicle.color} />

                    <DisplayFieldValue label="Registration plate" value={newVehicle.license_plate}/>

                    <DisplayFieldValue label="VIN" value={newVehicle.vin}/>

                    <DisplayFieldValue label="Body type" value={newVehicle.body_type}/>
                </div>
                {loading ? (
                    <div style={{display: 'flex', justifyContent: 'center', marginTop: '20px'}}>
                        <Circular/>
                    </div>
                ) : <SubmitBtn text="Finish" breadcrumbObject={breadcrumbObject}/>}

            </form>
        </>
    );
}
